import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "../../components/core/button.core";
import Input from "../../components/core/input.core";
import TextArea from "../../components/core/textarea.core";
import { useToast } from "../../../hooks/use-toast";
import {
  type IzCampaignFormBrand,
  MilestoneRewardType,
  zCampaignFormBrand,
} from "../../../z/brand/campaign-form.brand.z";
import {
  SocialCreatorType,
  SocialGender,
  SocialPlatforms,
} from "../../../types/models/social.models.types";
import { SocialPostType } from "../../../types/models/campaign.models.types";
import Select from "../../components/core/select.core";
import ImageUpload from "../../components/core/imageupload.core";
import Textarea from "../../components/core/textarea.core";
import PrefixTextArea from "../../components/core/prefix-textarea.core";
import { findErrorMessage, handleHttpError } from "../../../utils/utils";

import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../components/core/sheet.core";
import AssetUpload from "../../components/core/assetupload.core";
import DocumentSmall from "../../../assets/svgs/campaigns/doc.svg";
import DeleteIcon from "../../../assets/svgs/common/delete.svg";
import UploadIcon from "../../../assets/svgs/common/upload.svg";
import { ThreeDWrapper } from "../../components/core/3d-wrapper.core";
import LinkUpload from "../../components/core/linkupload.core";
import LinkIcon from "../../assets/svgs/link.svg";
import MileStoneWhite from "../../components/core/milestone.core";
import BudgetCardWhite from "../../components/ui/budget-card.ui";
import { RadioCard } from "../../components/core/radio-cards.core";
import millify from "millify";
import {
  type ICPV,
  type IPayoutPercentage,
  useGetCampaignConfigHttp,
} from "../../hooks/http/campaigns/get-campaign-config.hook";
import Spinner from "../../../components/spinner.components";
import BoostIcon from "../../../assets/svgs/common/boost.svg";
import { useGetBrandCampaignByIdHttp } from "../../hooks/http/campaigns/get-brand-campaign-by-id.hook";
import type { IBrandCampaign } from "../../hooks/http/campaigns/get-brand-campaigns.hook";
import { Trash2 } from "lucide-react";
import DateInput from "../../components/core/date-input.core";
import {
  type IAsset,
  useUpdateCampaignHttp,
} from "../../hooks/http/campaigns/update-brand-campaign.hook";
import { useCreateAssetCommonHttp } from "../../../hooks/http/common/utils/asset/create-asset.common.http";
import type { IAssetMinimal } from "../../../types/models/asset.models.types";
import { useMakeCampaignLiveHttp } from "../../hooks/http/campaigns/make-campaign-live.hook";
import { z } from "zod";

const SocialPlatformsOptions = [
  {
    value: SocialPlatforms.instagram,
    label: "Instagram",
  },
];

const SocialPostTypeOptions = [
  {
    value: SocialPostType.REEL,
    label: "Reel",
  },
];

enum CampaignStep {
  DETAILS = 1,
  CLIPS = 2,
  BUDGET = 3,
}

enum SheetType {
  "ADD_ASSET" = "ADD_ASSET",
  "ADD_LINK" = "ADD_LINK",
  "MAKE_CAMPAIGN_LIVE" = "MAKE_CAMPAIGN_LIVE",
}
// Make campaign live
const MakeCampaignLiveSheet = ({
  form,
  onSubmit,
  updating,
}: {
  form: ReturnType<typeof useForm<IzCampaignFormBrand>>;
  onSubmit: (data: IzCampaignFormBrand) => Promise<void>;
  updating: boolean;
}) => {
  const { toast } = useToast();
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const { execute: makeCampaignLiveHttp, fetching: makingCampaignLive } =
    useMakeCampaignLiveHttp();

  async function MakeCampaignLive() {
    if (!campaignId) return;
    const endDate = form.getValues().endDate;
    if (!endDate) {
      toast({
        content: "Please select an end date",
        toastType: "error",
      });
      return;
    }
    await onSubmit(form.getValues());

    const response = await makeCampaignLiveHttp({
      body: {
        endDate: endDate.toISOString(),
      },
      pathParams: { campaignId },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    toast({
      content: "Campaign live!",
      toastType: "success",
    });
    navigate("/");
  }

  return (
    <SheetContent className="bg-white p-0 border-border w-full max-w-2xl font-cabinetgrotesk">
      <SheetHeader className="mb-0 p-5 border-black border-b">
        <SheetTitle className="flex items-center gap-3">
          Make campaign live!
        </SheetTitle>
      </SheetHeader>

      <div className="flex flex-col justify-between gap-4 p-5 w-full h-[calc(100vh-80px)] overflow-y-auto">
        <div className="flex flex-col gap-4 w-full text-black">
          <Controller
            name="endDate"
            control={form.control}
            render={({ field: { value, onChange, ref } }) => (
              <DateInput
                ref={ref}
                label="Set end Date"
                placeholder="Select end date"
                value={value}
                onChange={onChange}
                error={form.formState.errors.endDate?.message}
                required
                fullWidth
                disabledDays={(date) => {
                  const startDate = form.watch("startDate");
                  return date < (startDate || new Date());
                }}
              />
            )}
          />
        </div>

        <SheetFooter className="w-full">
          <div className="border-t w-full">
            <Button
              type="button"
              onClick={MakeCampaignLive}
              isLoading={updating || makingCampaignLive}
              className="py-4 border border-black rounded-xl w-full font-semibold text-base text-black"
            >
              <BoostIcon className="mr-2" />
              Make campaign live!
            </Button>
          </div>
        </SheetFooter>
      </div>
    </SheetContent>
  );
};

// Separate component for Add Asset Sheet
const AddAssetSheetContent = ({
  tempAsset,
  setTempAsset,
  handleAddAsset,
}: {
  tempAsset: { file: File | null; notes: string; editIndex?: number };
  setTempAsset: React.Dispatch<
    React.SetStateAction<{
      file: File | null;
      notes: string;
      editIndex?: number;
    }>
  >;
  handleAddAsset: () => void;
}) => {
  const isEditMode = tempAsset.editIndex !== undefined;

  return (
    <SheetContent className="bg-white p-0 border-border w-full max-w-2xl font-cabinetgrotesk">
      <SheetHeader className="mb-0 p-5 border-black border-b">
        <SheetTitle className="flex items-center gap-3">
          {isEditMode ? "Edit your asset" : "Add your asset"}
        </SheetTitle>
      </SheetHeader>

      <div className="flex flex-col justify-between gap-4 p-5 w-full h-[calc(100vh-80px)] overflow-y-auto">
        <div className="flex flex-col gap-4 w-full">
          {/* File Upload */}
          {!tempAsset.file ? (
            // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
            <div
              className="flex justify-center items-center gap-5 px-6 py-6 border border-black border-dashed rounded-xl text-black transition-colors cursor-pointer"
              onClick={() => {
                document.getElementById("asset-file-input")?.click();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  document.getElementById("asset-file-input")?.click();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <UploadIcon />
              <p className="font-medium text-base">Upload files</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex justify-between items-center gap-4 bg-gray-50 px-4 py-3 border border-black border-dashed rounded-xl">
                <div className="flex items-center gap-3">
                  <ThreeDWrapper
                    hover={false}
                    className="flex justify-center items-center p-0 rounded-lg w-10 h-10 text-black text-xl"
                  >
                    <DocumentSmall />
                  </ThreeDWrapper>
                  <p className="font-medium text-black text-sm truncate">
                    {tempAsset.file.name.length > 30
                      ? `${tempAsset.file.name.substring(0, 30)}...`
                      : tempAsset.file.name}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // In edit mode, allow selecting new file. Keep the editIndex and notes
                    setTempAsset((prev) => ({
                      file: null,
                      notes: "",
                      editIndex: prev.editIndex,
                    }));
                  }}
                  className="text-red-500 hover:text-red-600 transition-colors"
                >
                  <DeleteIcon />
                </button>
              </div>
            </div>
          )}

          <input
            id="asset-file-input"
            type="file"
            accept="image/jpeg,image/jpg,image/png,video/mp4,video/quicktime"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                // Keep editIndex and notes when replacing file
                setTempAsset((prev) => ({
                  file,
                  notes: prev.notes,
                  editIndex: prev.editIndex,
                }));
              }
              // Reset input value to allow re-uploading same file
              e.target.value = "";
            }}
          />

          {/* Notes */}
          {tempAsset.file && (
            <div className="flex flex-col gap-2 text-black">
              <TextArea
                placeholder="Note here..."
                label="Notes"
                rows={5}
                fullWidth
                value={tempAsset.notes}
                onChange={(e) =>
                  setTempAsset((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
              />
            </div>
          )}
        </div>

        <SheetFooter className="w-full">
          <div className="border-t w-full">
            <Button
              type="button"
              onClick={handleAddAsset}
              disabled={!tempAsset.file}
              className="py-4 border border-black rounded-xl w-full font-semibold text-base text-black"
            >
              {isEditMode ? "Update asset →" : "Add asset →"}
            </Button>
          </div>
        </SheetFooter>
      </div>
    </SheetContent>
  );
};

// Separate component for Add Link Sheet
const AddLinkSheetContent = ({
  tempLink,
  setTempLink,
  handleAddLink,
}: {
  tempLink: { title: string; url: string; notes: string; editIndex?: number };
  setTempLink: React.Dispatch<
    React.SetStateAction<{
      title: string;
      url: string;
      notes: string;
      editIndex?: number;
    }>
  >;
  handleAddLink: () => void;
}) => {
  const isEditMode = tempLink.editIndex !== undefined;

  return (
    <SheetContent className="bg-white p-0 border-border w-full max-w-2xl font-cabinetgrotesk text-black">
      <SheetHeader className="mb-0 p-5 border-black border-b">
        <SheetTitle className="flex items-center gap-3">
          {isEditMode ? "Edit your link" : "Add your link"}
        </SheetTitle>
      </SheetHeader>

      <div className="flex flex-col justify-between gap-4 p-5 w-full h-[calc(100vh-80px)] overflow-y-auto">
        <div className="flex flex-col gap-4 w-full">
          {/* Link Title Input */}
          <Input
            label="Link title"
            placeholder="Title here..."
            value={tempLink.title}
            onChange={(e) =>
              setTempLink((prev) => ({ ...prev, title: e.target.value }))
            }
            fullWidth
          />

          {/* Link URL Input */}
          <div className="flex flex-col gap-2">
            <label className="font-medium text-sm">Enter link here</label>
            <div className="relative">
              <Input
                prefixIcon={<LinkIcon />}
                placeholder="Link goes here..."
                value={tempLink.url}
                type="url"
                onChange={(e) => {
                  const value = e.target.value;
                  setTempLink((prev) => ({ ...prev, url: value }));
                }}
                className="pl-12"
                fullWidth
                error={
                  tempLink.url &&
                  !z.string().url().safeParse(tempLink.url).success
                    ? "Please enter a valid URL"
                    : undefined
                }
              />
            </div>
          </div>
        </div>

        <SheetFooter className="w-full">
          <div className="border-t w-full">
            <Button
              type="button"
              onClick={handleAddLink}
              disabled={!tempLink.title || !tempLink.url}
              className="py-4 border border-black rounded-xl w-full font-semibold text-base text-black"
            >
              {isEditMode ? "Update link →" : "Add link →"}
            </Button>
          </div>
        </SheetFooter>
      </div>
    </SheetContent>
  );
};

export default function UpdateCampaignPartial() {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState<CampaignStep>(
    CampaignStep.DETAILS
  );

  const [, setBrandCampaign] = useState<IBrandCampaign>();

  const { execute: getBrandCampaignByIdHttp, fetching: gettingBrandCampaign } =
    useGetBrandCampaignByIdHttp();

  const { execute: updateCampaignHttp, fetching: updatingCampaign } =
    useUpdateCampaignHttp();

  const { campaignId } = useParams();

  const [isSheetOpen, setIsSheetOpen] = useState({
    open: false,
    type: SheetType.ADD_ASSET,
  });

  const [cpvConfig, setCpvConfig] = useState<ICPV>();
  const [payoutPercentageConfig, setPayoutPercentageConfig] =
    useState<IPayoutPercentage>();

  const { execute: getCampaignConfigHttp, fetching: gettingCampaignConfig } =
    useGetCampaignConfigHttp();

  const { execute: uploadAssetHttp, fetching: uploadingAsset } =
    useCreateAssetCommonHttp();

  const [cpv, setCpv] = useState(0);
  const [maxBudget, setMaxBudget] = useState(0);

  const [cpm, setCPM] = useState("milestone");

  // Platform fee state - will come from API
  const [platformFee] = useState(20);

  const [tempAsset, setTempAsset] = useState<{
    file: File | null;
    notes: string;
    editIndex?: number;
  }>({
    file: null,
    notes: "",
    editIndex: undefined,
  });

  const [tempLink, setTempLink] = useState<{
    title: string;
    url: string;
    notes: string;
    editIndex?: number;
  }>({
    title: "",
    url: "",
    notes: "",
    editIndex: undefined,
  });

  async function updateCampaign() {
    if (!campaignId) return;

    try {
      const data = form.getValues();

      // Upload cover image if dirty
      if (
        dirtyFields.coverImageFile &&
        data.coverImageFile &&
        data.coverImageFile.length > 0
      ) {
        const uploadedCoverImage = await uploadAsset(
          data.coverImageFile[0].file
        );
        if (!uploadedCoverImage) {
          return toast({
            content: "Failed to upload cover image. Please try again",
            toastType: "error",
          });
        }
        form.setValue(
          "coverImage",
          {
            id: uploadedCoverImage.id,
            url: uploadedCoverImage.url,
            name: uploadedCoverImage.name,
          },
          { shouldDirty: true }
        );

        data.coverImage = {
          id: uploadedCoverImage.id,
          url: uploadedCoverImage.url,
          name: uploadedCoverImage.name,
        };
      }

      // Upload assets if dirty
      if (
        dirtyFields.assetFile &&
        data.assetFile &&
        data.assetFile.length > 0
      ) {
        const uploadedAssets = await Promise.all(
          data.assetFile.map((file) =>
            uploadAsset(file.file, file.note || null)
          )
        );
        const failedAssetUploads = uploadedAssets.filter((asset) => !asset);

        if (failedAssetUploads.length > 0) {
          toast({
            content: "Failed to upload some assets. Please try again",
            toastType: "error",
          });
          return;
        }

        const newAssets = uploadedAssets
          .filter((asset): asset is IAssetMinimal => asset !== false)
          .map((asset) => ({
            id: asset.id,
            url: asset.url,
            name: asset.name,
          }));

        form.setValue("assetFile", []);
        form.setValue(
          "assets",
          [
            ...(data.assets || []), // Handle null/undefined case
            ...newAssets.map((asset) => ({
              id: asset.id,
              url: asset.url,
              name: asset.name            
              
            })),
          ],
          { shouldDirty: true }
        );

        data.assets = [
          ...(data.assets || []), // Handle null/undefined case
          ...newAssets.map((asset) => ({
            id: asset.id,
            url: asset.url,
            name: asset.name,
          })),
        ];
      }

      // Handle milestone product uploads if dirty
      let updatedMilestones = data.milestones;
      if (dirtyFields.milestones) {
        updatedMilestones = await Promise.all(
          data.milestones.map(async (milestone, index) => {
            // Check if this specific milestone has a productFile that needs uploading
            const milestoneField = dirtyFields.milestones?.[index];
            if (milestoneField?.productFile && milestone.productFile) {
              // Upload the product image
              const uploadedProduct = await uploadAsset(
                milestone.productFile.file
              );

              if (!uploadedProduct) {
                toast({
                  content: `Failed to upload product for milestone ${index + 1}`,
                  toastType: "error",
                });
                throw new Error("Product upload failed");
              }

              // Return milestone with uploaded product
              return {
                count: milestone.count,
                payout: milestone.payout,
                product: {
                  image: {
                    id: uploadedProduct.id,
                    name: uploadedProduct.name,
                    url: uploadedProduct.url,
                  },
                  name: milestone.productFile.name,
                  description: null,
                },
                productFile: null, // Clear productFile after upload
              };
            }

            // Return milestone as-is if no product upload needed
            return {
              count: milestone.count,
              payout: milestone.payout,
              product: milestone.product,
              productFile: null,
            };
          })
        );
      }

      const response = await updateCampaignHttp({
        body: {
          name: dirtyFields.name ? data.name : undefined,
          startDate: data.startDate?.toISOString(),
          coverImage:
            dirtyFields.coverImage || dirtyFields.coverImageFile
              ? data.coverImage
                ? data.coverImage.id
                : null
              : undefined,
          socialPlatform: dirtyFields.socialPlatform
            ? data.socialPlatform
            : undefined,
          socialPostType: dirtyFields.socialPostType
            ? data.socialPostType
            : undefined,
          socialCreatorType: dirtyFields.socialCreatorType
            ? data.socialCreatorType
            : undefined,
          minimumFollowers: dirtyFields.minimumFollowers
            ? data.minimumFollowers
            : undefined,
          endDate: dirtyFields.endDate
            ? data.endDate?.toISOString()
            : undefined,
          budget: dirtyFields.budget ? data.budget : undefined,
          milestoneRewardType: dirtyFields.milestoneRewardType
            ? data.milestoneRewardType
            : undefined,
          milestones: dirtyFields.milestones
            ? updatedMilestones.map((milestone) => ({
                count: milestone.count,
                payout: milestone.payout ? milestone.payout : null,
                product: milestone.product
                  ? {
                      image: milestone.product.image.id,
                      name: milestone.product.name,
                      description: milestone.product.description || "",
                    }
                  : null,
              }))
            : undefined,
          cpv:
            cpm === "cpm"
              ? dirtyFields.cpv
                ? data.cpv
                  ? {
                      costPerView: data.cpv.costPerView,
                      maxPayout: data.cpv.maxPayout,
                    }
                  : null
                : undefined
              : undefined,
          assets: dirtyFields.assets
            ? [...data.assets.map((asset) => asset.id)]
            : undefined,
          links: dirtyFields.linkAssets
            ? data.linkAssets.map((link) => ({
                title: link.title,
                url: link.url,
              }))
            : undefined,
          hashtags: dirtyFields.hashtags ? data.hashtags : undefined,
          mentions: dirtyFields.mentions ? data.mentions : undefined,
          aboutCampaign: dirtyFields.aboutCampaign
            ? data.aboutCampaign
            : undefined,
          aboutBrand: dirtyFields.aboutBrand ? data.aboutBrand : undefined,
          do: dirtyFields.do ? data.do : undefined,
          dont: dirtyFields.dont ? data.dont : undefined,
        },
        pathParams: { campaignId },
      });

      if (!response.success) {
        return handleHttpError(response, toast);
      }

      // reset dirty fields
      form.reset(form.getValues());

        form.setValue(
        "assets",
        (response.data.campaign?.assets || [])
          .map((asset: IAsset | string) => {
            // Handle case where asset is just an ID string or a full asset object
            if (typeof asset !== "string") {
              return {
                id: asset.id,
                url: asset.url,
                name: asset.name,
                note: asset.note || null,
              };
            }
            // Handle case where asset is a full IAsset object
            return undefined;
          })
          .filter(
            (asset): asset is { id: string; url: string; name: string; note: string | null } =>
              asset !== undefined
          )
      );

      

      // Close sheet and navigate
      closeSheet();
    } catch (error) {
      console.error(error);
      toast({
        content: "Failed to update campaign. Please try again",
        toastType: "error",
      });
    }
  }

  // Add uploadAsset function (reuse from create campaign)
  async function uploadAsset(file: File, note?: string | null) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("note", note || "");
    const response = await uploadAssetHttp({
      body: formData,
    });
    if (!response.success) {
      return false;
    }
    return response.data.asset;
  }

  async function getCampaignConfig() {
    const response = await getCampaignConfigHttp({ body: null });
    if (!response.success) {
      handleHttpError(response, toast);
      return;
    }

    // Set config first
    setCpvConfig(response.data.cpv);
    setPayoutPercentageConfig(response.data.payoutPercentage);

    // Then set the values based on the response data
    setCpv(response.data.cpv.max);
    setMaxBudget(response.data.payoutPercentage.max);

    // Set form default values
    form.setValue("cpv.costPerView", response.data.cpv.default || 0);
    form.setValue("cpv.maxPayout", response.data.payoutPercentage.default || 0);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (cpm === "cpm") {
      getCampaignConfig();
    }
  }, [cpm]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getBrandCampaign();
  }, []);

  const form = useForm<IzCampaignFormBrand>({
    resolver: zodResolver(zCampaignFormBrand),
    defaultValues: {
      name: "",
      coverImage: null,
      socialPostType: SocialPostType.REEL,
      socialPlatform: SocialPlatforms.instagram,
      socialCreatorType: SocialCreatorType.face,
      genderPreference: SocialGender.prefernotsay,
      startDate: new Date(),
      endDate: null,
      milestones: [],
      milestoneRewardType: null,
      assets: [],
      hashtags: null,
      mentions: null,
      aboutCampaign: null,
      do: null,
      dont: null,
      aboutPayment: null,
      aboutBrand: null,
      cpv: {
        costPerView: 0,
        maxPayout: 0,
      },
    },
    mode: "onChange",
    shouldFocusError: false,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    watch,
  } = form;

  async function getBrandCampaign() {
    if (!campaignId) return;
    const response = await getBrandCampaignByIdHttp({
      body: null,
      pathParams: { campaignId },
    });
    if (!response.success) {
      handleHttpError(response, toast);
      return;
    }
    setBrandCampaign(response.data.campaign);

    // Set all form values
    form.setValue("name", response.data.campaign.name);
    form.setValue("coverImage", response.data.campaign.coverImage);
    form.setValue("do", response.data.campaign.do);
    form.setValue("dont", response.data.campaign.dont);
    form.setValue("aboutCampaign", response.data.campaign.aboutCampaign);
    form.setValue("hashtags", response.data.campaign.hashtags);
    form.setValue("mentions", response.data.campaign.mentions);
    form.setValue("socialPlatform", response.data.campaign.socialPlatform);
    form.setValue("socialPostType", response.data.campaign.socialPostType);
    form.setValue("aboutBrand", response.data.campaign.aboutBrand);
    form.setValue(
      "socialCreatorType",
      response.data.campaign.socialCreatorType
    );
    form.setValue("budget", response.data.campaign.budget);
    form.setValue("minimumFollowers", response.data.campaign.minimumFollowers);
    form.setValue("assets", response.data.campaign.assets);
    form.setValue(
      "milestones",
      response.data.campaign.milestones.map((milestone) => ({
        ...milestone,
        product: milestone.product
          ? {
              image: milestone.product.image,
              name: milestone.product.name,
              description: milestone.product.description,
            }
          : null,
        productFile: null,
      }))
    );
    form.setValue(
      "milestoneRewardType",
      (response.data.campaign.milestoneRewardType as MilestoneRewardType) ||
        null
    );
    form.setValue("linkAssets", response.data.campaign.links);

    // CRITICAL: Reset the form to mark current state as the new "clean" baseline
    form.reset(form.getValues(), {
      keepDefaultValues: false,
    });
  }

  // Watch form values for real-time calculation
  const budgetValue = watch("budget") || 0;
  const cpvValue = watch("cpv.costPerView") || 0;
  const maxPayoutValue = watch("cpv.maxPayout") || 0;

  // Calculate budget after platform fee
  const getEffectiveBudget = () => {
    return budgetValue - (budgetValue * platformFee) / 100;
  };

  // Calculate expected views in real-time
  const calculateExpectedViews = () => {
    if (cpvValue > 0 && budgetValue > 0) {
      const effectiveBudget = getEffectiveBudget();
      return Math.round((effectiveBudget / cpvValue) * 1000);
    }
    return 0;
  };

  // Calculate max posts based on max payout percentage
  const calculateMaxPosts = () => {
    if (maxPayoutValue > 0 && budgetValue > 0) {
      const effectiveBudget = getEffectiveBudget();
      const maxPayoutAmount = (effectiveBudget * maxPayoutValue) / 100;
      if (maxPayoutAmount > 0) {
        return Math.floor(effectiveBudget / maxPayoutAmount);
      }
    }
    return 0;
  };

  // Calculate budget per creator
  const calculateBudgetPerCreator = () => {
    if (maxPayoutValue > 0 && budgetValue > 0) {
      const effectiveBudget = getEffectiveBudget();
      return Math.round((effectiveBudget * maxPayoutValue) / 100);
    }
    return 0;
  };

  // Navigation handlers
  const handleNext = async () => {
    let isValid = false;
    // Validate current step fields
    switch (currentStep) {
      case CampaignStep.DETAILS:
        isValid = await trigger([
          "name",
          "socialPostType",
          "socialCreatorType",
          "minimumFollowers",
          "coverImage",
          "hashtags",
          "mentions",
          "aboutCampaign",
          "do",
          "dont",
        ]);
        break;
      case CampaignStep.CLIPS:
        isValid = await trigger(["assetFile"]);
        break;
      case CampaignStep.BUDGET:
        isValid = await trigger(["budget", "cpv", "milestones"]);
        break;
    }

    if (isValid && currentStep < CampaignStep.BUDGET) {
      setCurrentStep((currentStep + 1) as CampaignStep);
    }
  };

  const handleBack = () => {
    if (currentStep > CampaignStep.DETAILS) {
      setCurrentStep((currentStep - 1) as CampaignStep);
    }
  };

  const onSubmit = async (data: IzCampaignFormBrand) => {
    console.log("Campaign data:", data);
    // TODO: Submit campaign data via HTTP hook
    await updateCampaign();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];
      console.log(key);
      if (key) {
        const error =
          form.formState.errors[key as keyof typeof form.formState.errors];
        const message = findErrorMessage(error) || "An error occurred";

        toast({
          content: message,
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors]);

  const closeSheet = () => {
    document.getElementById("closeSheet")?.click();
    setTimeout(() => {
      setIsSheetOpen({ open: false, type: isSheetOpen.type });
      setTempAsset({ file: null, notes: "", editIndex: undefined });
      setTempLink({ title: "", url: "", notes: "", editIndex: undefined });
    }, 100);
  };

  const handleAddAsset = () => {
    if (tempAsset.file) {
      const currentFiles =
        (form.getValues("assetFile") as Array<{
          file: File;
          note?: string;
        }>) || [];

      if (tempAsset.editIndex !== undefined) {
        // Edit existing asset - replace the file at the index
        const updatedFiles = [...currentFiles];
        updatedFiles[tempAsset.editIndex] = {
          file: tempAsset.file,
          note: tempAsset.notes,
        };
        form.setValue(
          "assetFile",
          updatedFiles.map((item) => ({
            ...item,
            note: item.note || null,
          })) as Array<{ file: File; note: string | null }>,
          {
            shouldDirty: true,
          }
        );
      } else {
        // Add new asset
        form.setValue(
          "assetFile",
          [
            ...currentFiles,
            { file: tempAsset.file, note: tempAsset.notes },
          ] as Array<{
            file: File;
            note: string | null;
          }>,
          {
            shouldDirty: true,
          }
        );
      }

      closeSheet();
    }
  };

  const handleAddLink = () => {
    if (tempLink.title && tempLink.url) {
      const currentLinks =
        (form.getValues("linkAssets") as Array<{
          title: string;
          url: string;
          notes?: string;
        }>) || [];

      if (tempLink.editIndex !== undefined) {
        // Edit existing link
        const updatedLinks = [...currentLinks];
        updatedLinks[tempLink.editIndex] = {
          title: tempLink.title,
          url: tempLink.url,
          notes: tempLink.notes,
        };
        form.setValue(
          "linkAssets",
          updatedLinks as Array<{
            title: string;
            url: string;
            notes?: string;
          }>,
          {
            shouldDirty: true,
          }
        );
      } else {
        // Add new link
        form.setValue(
          "linkAssets",
          [
            ...currentLinks,
            { title: tempLink.title, url: tempLink.url, notes: tempLink.notes },
          ] as Array<{
            title: string;
            url: string;
            notes?: string;
          }>,
          {
            shouldDirty: true,
          }
        );
      }

      closeSheet();
    }
  };

  const handleEditAsset = (index: number) => {
    const currentFiles = form.getValues("assetFile") as Array<{
      file: File;
      note?: string;
    }>;
    const asset = currentFiles[index];
    if (asset) {
      setTempAsset({
        file: asset.file,
        notes: asset.note || "",
        editIndex: index,
      });
      setIsSheetOpen({
        open: true,
        type: SheetType.ADD_ASSET,
      });
    }
  };

  const handleEditLink = (index: number) => {
    const currentLinks = form.getValues("linkAssets") as Array<{
      title: string;
      url: string;
      notes?: string;
    }>;
    const link = currentLinks[index];
    if (link) {
      setTempLink({
        title: link.title,
        url: link.url,
        notes: link.notes || "",
        editIndex: index,
      });
      setIsSheetOpen({
        open: true,
        type: SheetType.ADD_LINK,
      });
    }
  };

  const dirtyFields = form.formState.dirtyFields;
  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;

  if (gettingBrandCampaign) {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <Spinner width={20} />
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <div className="flex flex-col items-center w-full h-full font-cabinetgrotesk">
        {/* Header */}
        <div className="flex justify-center items-center py-6 border-black border-b w-full font-semibold text-2xl">
          Create your Campaign
        </div>

        {/* Step Indicators */}
        <div className="flex justify-around items-center py-6 border-black border-b w-full text-base">
          <div
            className={`flex flex-col items-center gap-2 transition-opacity cursor-pointer ${
              currentStep === CampaignStep.DETAILS
                ? "opacity-100 font-semibold"
                : "opacity-30"
            }`}
          >
            <p>
              [1] <span>Campaign details</span>
            </p>
            <div
              className={`bg-secondary rounded-full w-full h-1 ${
                currentStep === CampaignStep.DETAILS
                  ? "opacity-100 font-semibold"
                  : "opacity-30"
              } `}
            />
          </div>

          <div
            className={`flex flex-col items-center gap-2 transition-opacity cursor-pointer ${
              currentStep === CampaignStep.CLIPS
                ? "opacity-100 font-semibold"
                : "opacity-30"
            }`}
          >
            <p>
              [2] <span>Campaign clips</span>
            </p>

            <div
              className={`bg-secondary rounded-full w-full h-1 ${
                currentStep === CampaignStep.CLIPS
                  ? "opacity-100 font-semibold"
                  : "opacity-30"
              } `}
            />
          </div>

          <div
            className={`flex flex-col items-center gap-2 transition-opacity cursor-pointer ${
              currentStep === CampaignStep.BUDGET
                ? "opacity-100 font-semibold"
                : "opacity-30"
            }`}
          >
            <p>
              [3] <span>Campaign budget & targeting</span>
            </p>

            <div
              className={`bg-secondary rounded-full w-full h-1 ${
                currentStep === CampaignStep.BUDGET
                  ? "opacity-100 font-semibold"
                  : "opacity-30"
              } `}
            />
          </div>
        </div>

        {/* Form Content */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 px-6 py-8 w-full overflow-y-auto"
        >
          {/* Step 1: Campaign Details */}
          {currentStep === CampaignStep.DETAILS && (
            <div className="flex flex-col gap-6">
              <div className="gap-6 grid grid-cols-2">
                <Controller
                  name="coverImageFile"
                  control={control}
                  render={({ field: { value, onChange, ref } }) =>
                    form.watch("coverImage") ? (
                      <div className="flex flex-col gap-2">
                        <label className="font-cabinetgrotesk md:font-medium text-sm md:text-lg leading-none tracking-[-0.36px]">
                          Upload cover image
                        </label>
                        <div className="relative flex justify-between items-center gap-4 py-9 pr-4 pl-4 border border-black rounded-xl h-16">
                          <div className="flex items-center gap-2">
                            <div className="flex justify-center items-center bg-gray-100 rounded-lg w-8 h-8 text-black">
                              <DocumentSmall />
                            </div>
                            <p className="w-32 md:w-64 font-semibold text-nowrap text-sm truncate overflow-hidden">
                              {form.getValues("coverImage")?.name}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              form.setValue("coverImage", null);
                            }}
                          >
                            <Trash2 className="w-5 h-5 text-red-500" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <ImageUpload
                        ref={ref} // Pass ref from Controller
                        label="Upload cover image"
                        value={value?.[0]?.file || form.getValues("coverImage")}
                        onChange={(file) => {
                          if (file) {
                            onChange([{ file }]);
                          } else {
                            onChange([]);
                          }
                        }}
                        error={errors.coverImageFile?.message}
                        description="Max file size: 5MB (Supported formats: .png, .jpg, .jpeg)"
                        fullWidth
                      />
                    )
                  }
                />
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Campaign Name"
                      placeholder="Enter campaign name"
                      error={errors.name?.message}
                      variant="default"
                      fullWidth
                      required
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="socialPlatform"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Platform Type"
                      placeholder="Select platform type"
                      options={SocialPlatformsOptions}
                      error={errors.socialPlatform?.message}
                      fullWidth
                      value={field.value || ""}
                      onChange={(value) =>
                        field.onChange(value as SocialPlatforms)
                      }
                    />
                  )}
                />

                <Controller
                  name="socialPostType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Content Type"
                      placeholder="Select content type"
                      options={SocialPostTypeOptions}
                      error={errors.socialPostType?.message}
                      fullWidth
                      value={field.value || ""}
                      onChange={(value) =>
                        field.onChange(value as SocialPostType)
                      }
                    />
                  )}
                />
                <div className="col-span-2">
                  <Controller
                    name="aboutCampaign"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        label="About campaign"
                        placeholder="Enter about campaign"
                        error={errors.aboutCampaign?.message}
                        fullWidth
                        required
                        rows={5}
                        {...field}
                        value={field.value || ""}
                      />
                    )}
                  />
                </div>
                <div className="col-span-2">
                  <Controller
                    name="aboutBrand"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        label="About brand"
                        placeholder="Enter about brand"
                        error={errors.aboutBrand?.message}
                        fullWidth
                        required
                        rows={5}
                        {...field}
                        value={field.value || ""}
                      />
                    )}
                  />
                </div>
                <Controller
                  name="do"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      label="Do's"
                      placeholder="Enter do's"
                      error={errors.do?.message}
                      required
                      fullWidth
                      rows={5}
                      {...field}
                      value={field.value || ""}
                    />
                  )}
                />
                <Controller
                  name="dont"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      label="Don't"
                      placeholder="Enter don't"
                      error={errors.dont?.message}
                      required
                      fullWidth
                      rows={5}
                      {...field}
                      value={field.value || ""}
                    />
                  )}
                />
                <PrefixTextArea
                  form={form}
                  prefix="#"
                  name="hashtags"
                  label="Mandatory hashtags"
                  placeholder="Enter hashtags"
                  required={false}
                />

                <PrefixTextArea
                  form={form}
                  prefix="@"
                  name="mentions"
                  label="Mentions"
                  placeholder="Enter mentions"
                  required={false}
                />
              </div>
            </div>
          )}

          {/* Step 2: Campaign Brief */}
          {currentStep === CampaignStep.CLIPS && (
            <div className="flex flex-col gap-6 h-full">
              <div className="gap-6 grid grid-cols-2">
                <AssetUpload
                  form={form}
                  name="assetFile"
                  trackingName="assets"
                  label="Upload assets to create content for this campaign "
                  placeholder="Upload files"
                  uploadType="all"
                  multiple
                  maxFiles={10}
                  description="File should not be more then 100 mb"
                  maxSize={100 * 1024 * 1024}
                  buttonLabel="Upload more"
                  onUploadClick={() =>
                    setIsSheetOpen({
                      open: true,
                      type: SheetType.ADD_ASSET,
                    })
                  }
                  onEdit={handleEditAsset} // Pass edit handler
                />

                <LinkUpload
                  form={form}
                  name="linkAssets"
                  label="Add links to create content for this campaign"
                  placeholder="Add links"
                  description="Reference links for creator"
                  buttonLabel="Add more"
                  onUploadClick={() =>
                    setIsSheetOpen({
                      open: true,
                      type: SheetType.ADD_LINK,
                    })
                  }
                  onEdit={handleEditLink} // Pass edit handler
                />
              </div>
            </div>
          )}

          {/* Step 3: Assets & Payment */}
          {currentStep === CampaignStep.BUDGET && (
            <div className="flex flex-col gap-6">
              <div className="gap-6 grid grid-cols-2">
                <Controller
                  name="minimumFollowers"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Minimum Followers"
                      className="-mt-1 p-5"
                      placeholder="Enter minimum followers"
                      error={errors.name?.message}
                      variant="default"
                      type="number"
                      fullWidth
                      {...field}
                      onChange={(e) => {
                        const value = Math.max(0, Number(e.target.value));
                        field.onChange(value);
                      }}
                    />
                  )}
                />

                <div className="col-span-2">
                  <Controller
                    name="budget"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="Enter Budget Amount"
                        className=""
                        placeholder="Enter budget amount"
                        error={errors.budget?.message}
                        variant="default"
                        type="number"
                        min="0"
                        fullWidth
                        required
                        {...field}
                        onChange={(e) => {
                          const value = Math.max(0, Number(e.target.value));
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                  {budgetValue > 0 && (
                    <p className="mt-2 text-gray-500 text-sm">
                      Effective budget after {platformFee}% platform fee: ₹
                      {getEffectiveBudget().toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="gap-6 grid grid-cols-2 col-span-2 w-full">
                  <div className="col-span-2 font-medium text-sm md:text-xl">
                    Select payout strategy
                  </div>
                  <RadioCard
                    label="Use fixed milestones"
                    value="milestone"
                    selectedValue={cpm}
                    onChange={(value) => {
                      setCPM(value);
                      form.setValue("cpv.costPerView", 0);
                      form.setValue("cpv.maxPayout", 0);
                    }}
                  />
                  {/* <RadioCard
                    label="Use CPM"
                    value="cpm"
                    disabled
                    selectedValue={cpm}
                    onChange={(value) => {
                      setCPM(value);
                      form.setValue("milestones", []);
                    }}
                  /> */}
                </div>

                {cpm === "milestone" && (
                  <div className="gap-6 grid grid-cols-3 col-span-2">
                    <div className="col-span-3 font-medium text-sm md:text-xl">
                      Select payout
                    </div>
                    <Controller
                      name="milestoneRewardType"
                      control={control}
                      render={({ field }) => (
                        <RadioCard
                          label="Money (₹)"
                          value={MilestoneRewardType.CASH}
                          selectedValue={field.value as MilestoneRewardType}
                          onChange={(value) => {
                            form.setValue("milestones", [], {
                              shouldDirty: true,
                            });
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="milestoneRewardType"
                      control={control}
                      render={({ field }) => (
                        <RadioCard
                          label="Barter"
                          value={MilestoneRewardType.PRODUCT}
                          selectedValue={field.value as MilestoneRewardType}
                          onChange={(value) => {
                            form.setValue("milestones", [], {
                              shouldDirty: true,
                            });
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="milestoneRewardType"
                      control={control}
                      render={({ field }) => (
                        <RadioCard
                          label="Both"
                          value={MilestoneRewardType.CASH_PRODUCT}
                          selectedValue={field.value as MilestoneRewardType}
                          onChange={(value) => {
                            form.setValue("milestones", [], {
                              shouldDirty: true,
                            });
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                    {form.watch("milestoneRewardType") ? (
                      <div className="col-span-3">
                        <MileStoneWhite
                          form={form}
                          payoutType={
                            form.getValues("milestoneRewardType") ||
                            MilestoneRewardType.CASH
                          }
                          label="Set milestones"
                          required
                        />
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                )}

                <div className="col-span-2">
                  {gettingCampaignConfig ? (
                    <div className="flex justify-center items-center py-20">
                      <Spinner width={20} />
                    </div>
                  ) : (
                    cpm === "cpm" &&
                    cpvConfig &&
                    payoutPercentageConfig && (
                      <div className="flex flex-col gap-6">
                        <BudgetCardWhite
                          form={form}
                          label="Enter Cost per thousand views"
                          name="cpv.costPerView"
                          maxBudget={cpv}
                          setMaxBudget={setCpv}
                          description="Amount paid per thousand views"
                          minValue={cpvConfig.min}
                          placeholder="Enter CPV amount"
                          valueType="decimal"
                          label1="Expected views"
                          label2="Budget usage"
                          value1={millify(calculateExpectedViews())}
                          value2={`₹${millify(budgetValue)}`}
                          note="Only 20% of the amount spent will be used as platform fee"
                        />
                        <BudgetCardWhite
                          form={form}
                          label="Enter Max payout per creator"
                          name="cpv.maxPayout"
                          maxBudget={maxBudget}
                          setMaxBudget={setMaxBudget}
                          minValue={payoutPercentageConfig.min}
                          description="Max payout per creator establishes how much a single creator can earn"
                          placeholder="Enter max payout amount"
                          valueType="percentage"
                          label1="Expected posts"
                          label2="Maximum earnings per creator"
                          value1={millify(calculateMaxPosts())}
                          value2={`₹${calculateBudgetPerCreator().toLocaleString()}`}
                        />
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Sheet - Now using separate components */}
        <Sheet
          open={isSheetOpen.open}
          onOpenChange={(open) => {
            if (!open && isSheetOpen.open) {
              closeSheet();
            }
          }}
        >
          {(() => {
            switch (isSheetOpen.type) {
              case SheetType.MAKE_CAMPAIGN_LIVE:
                return (
                  <MakeCampaignLiveSheet
                    form={form}
                    onSubmit={onSubmit}
                    updating={updatingCampaign}
                  />
                );
              case SheetType.ADD_ASSET:
                return (
                  <AddAssetSheetContent
                    tempAsset={tempAsset}
                    setTempAsset={setTempAsset}
                    handleAddAsset={handleAddAsset}
                  />
                );
              case SheetType.ADD_LINK:
                return (
                  <AddLinkSheetContent
                    tempLink={tempLink}
                    setTempLink={setTempLink}
                    handleAddLink={handleAddLink}
                  />
                );
              default:
                return null;
            }
          })()}
        </Sheet>

        {/* Navigation Footer */}
        <div className="bottom-0 sticky flex justify-between items-center gap-4 bg-white px-6 py-4 border-black border-t w-full">
          <Button
            type="button"
            variant="secondary"
            onClick={handleBack}
            disabled={currentStep === CampaignStep.DETAILS}
            className="px-8"
          >
            Back
          </Button>
          <div className="flex items-center gap-5">
            {currentStep === CampaignStep.BUDGET ? (
              <Button
                type="button"
                onClick={() => {
                  setIsSheetOpen({
                    open: true,
                    type: SheetType.MAKE_CAMPAIGN_LIVE,
                  });
                }}
                className="px-8"
              >
                Make Campaign live
              </Button>
            ) : (
              <Button type="button" onClick={handleNext} className="px-8">
                Next
              </Button>
            )}
            <Button
              disabled={!isDirty}
              isLoading={updatingCampaign || uploadingAsset}
              variant="secondary"
              onClick={handleSubmit(onSubmit)}
              className="w-48 text-nowrap"
            >
              Save changes
            </Button>
          </div>
        </div>
      </div>
    </FormProvider>
  );
}
